import { useLazyQuery } from '@apollo/client';
import type { FocusEventHandler } from 'react';
import { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { FabiJobSearchQueryGenerationUseCase } from '@xing-com/crate-common-graphql-types';
import { navigate } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameters } from '@xing-com/crate-hooks-use-search-parameters';
import {
  AI_SEARCH_INIT_PROMPT_PARAM,
  AI_SEARCH_ERROR_BANNER,
  AI_SEARCH_ERROR_TYPE,
  AI_FABI_TRACKING_TOKEN_PARAM,
  SEARCH_STATE_PARAMS,
  AI_EARN_MORE_MIN_SALARY,
} from '@xing-com/crate-jobs-constants';
import { JobSearchQueryGenerationDocument } from '@xing-com/crate-jobs-graphql';
import { buildSearchState } from '@xing-com/crate-jobs-helpers';
import { useAnimatedPlaceholder } from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';

import {
  setAIEarnMoreDialogSalary,
  useForceAIEarnMoreQuery,
  setShowNotEnoughProfileError,
} from '../../../apollo';
import {
  useLoadingPlaceholder,
  useSuggestionsTracking,
  useInitPromptOnLoad,
  type ForcedSubmitValues,
} from '../hooks';

import { QueryInputAnimation } from './query-input-animation';
import * as S from './query-input.styles';
import {
  suggestionsRenderer,
  isItemDisabled,
  RECENT_SEARCHS_TITLE,
  type Suggestion,
  useSuggestions,
} from './suggestions';

const QUERY_MAX_LENGTH = 1000;

type Props = {
  value: string;
  onChangeKeywords: (value: string) => void;
  onSubmit: (force?: ForcedSubmitValues, replace?: boolean) => void;
};

export const QueryInput: React.FC<Props> = ({
  value,
  onChangeKeywords,
  onSubmit,
}) => {
  const [truncatedQuery, setTruncatedQuery] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  const [forceAIEarnMoreQuery] = useForceAIEarnMoreQuery();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { formatMessage } = useIntl();
  const { isLoggedIn } = useLoginState();
  const loginUrl = useLoginAppUrl();
  const { setSearchParameters } = useSearchParameters();
  const track = useJobsNwtTracking();

  const onSuggestionError = (): void => {
    setSearchParameters(
      {
        [AI_SEARCH_INIT_PROMPT_PARAM]: '',
        [AI_SEARCH_ERROR_BANNER]: AI_SEARCH_ERROR_TYPE.FABI,
      },
      { removeEmpty: true }
    );
  };

  const [getSuggestions, { loading: areSuggestionsLoading }] = useLazyQuery(
    JobSearchQueryGenerationDocument,
    { fetchPolicy: 'network-only', onError: onSuggestionError }
  );
  const { suggestions, refetchRecentSearches } = useSuggestions();
  const trackSuggestionClick = useSuggestionsTracking();
  const { run: runLoadingAnimation, cancel: cancelLoadingAnimation } =
    useLoadingPlaceholder(inputRef);
  const runPlaceholderAnimation = useAnimatedPlaceholder(inputRef, {
    disable: areSuggestionsLoading,
  });
  const initPromptOnLoad = useInitPromptOnLoad();

  useEffect(() => {
    // First we check if the user is coming from a suggestion from Find Jobs
    if (initPromptOnLoad) {
      onLoadFabiSuggestion(initPromptOnLoad);
      return;
    }

    // Otherwise we just focus the input
    // The downshift library needs a click to open the suggestions (in case the input is empty)
    // See: https://github.com/downshift-js/downshift/blob/1743dfdaf616b0fda451d88600ca30d8c7c1714a/src/hooks/MIGRATION_V8.md#usecombobox-input-click
    inputRef.current?.click();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!forceAIEarnMoreQuery) {
      return;
    }

    onLoadFabiSuggestion(FabiJobSearchQueryGenerationUseCase.EarnMore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceAIEarnMoreQuery]);

  useEffect(() => {
    if (!value) {
      refetchRecentSearches();
      runPlaceholderAnimation();
    }

    if (truncatedQuery !== value) {
      setTruncatedQuery(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOnChange = (value: string): void => {
    onChangeKeywords(value.trim());
    setTruncatedQuery(value);
  };

  const handleOnKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowNotEnoughProfileError(false);
      onSubmit();
    }
  };

  const handleOnBlur: FocusEventHandler<HTMLTextAreaElement> = (e): void => {
    // scroll to top of textarea text
    e.target.scrollTo(0, 0);

    // When unfocusing the query, the textarea will shrink to the original
    // height (3 lines). We want to show an ellipsis if the text is longer than
    // that. We can manipulate the value directly without React, so we don't
    // lose the original text (in a state). We keep removing content until the
    // scrollHeight is the same as the original height. The loop has a limit of
    // iterations to avoid infinite loops (Shouldn't happen, but just in case)
    for (
      let i = 0;
      i < QUERY_MAX_LENGTH && e.target.scrollHeight > e.target.clientHeight;
      ++i
    ) {
      e.target.value = e.target.value.slice(0, -4) + '...';
    }
    setTruncatedQuery(e.target.value);
  };

  const handleOnFocus = (): void => {
    // Restore the original text
    setTruncatedQuery(value);
  };

  const handleOnSelect = (value: Suggestion): void => {
    setShowNotEnoughProfileError(false);

    if (typeof value === 'string') {
      // Menu - should be skipped
      if (value === RECENT_SEARCHS_TITLE) {
        return;
      }

      // Fabi cases
      trackSuggestionClick(value);

      if (!isLoggedIn) {
        navigate(
          `${loginUrl}?dest_url=${encodeURIComponent(`${XING_JOBS_SEARCH_KI_PATH}?${AI_SEARCH_INIT_PROMPT_PARAM}=${value}`)}`
        );
        return;
      }

      onLoadFabiSuggestion(value);
      return;
    }

    track({
      element: 'recent_searches',
      elementDetail: 'auto_complete_option',
      page: 'jobs_search/serp',
      event: 'clicked',
      eventSchema: 'basic',
    });

    // Since we allow the user to select the same option, we need to update the
    // keywords as otherwise when clicking the same option the keywords input
    // is not filled. Also, as we are using the URL params to keep the state of
    // the search, when clicking the same recent search, no URL will be
    // updated, meaning that the keywords input is not filled by default, and
    // we need to update it manually
    onChangeKeywords(value.body?.keywords ?? '');

    const params = new URLSearchParams(buildSearchState(value.body ?? {}));
    navigate(`${XING_JOBS_SEARCH_KI_PATH}?${params.toString()}`);
  };

  const onLoadFabiSuggestion = async (
    useCase: FabiJobSearchQueryGenerationUseCase
  ): Promise<void> => {
    runLoadingAnimation();
    const { data } = await getSuggestions({
      variables: {
        input: {
          consumer: 'loggedin.web.jobs.conv_search_landing.center',
          useCase,
          userQuery: '',
        },
      },
    });
    cancelLoadingAnimation();

    const result = data?.fabiJobSearchQueryGeneration;
    if (result?.__typename !== 'FabiJobSearchQueryGenerationResult') {
      onSuggestionError();
      return;
    }

    const { earnMoreSpecific, enoughProfileData: hasEnoughProfileData } =
      result;

    if (earnMoreSpecific?.salaryPreferencePresent === false) {
      setAIEarnMoreDialogSalary(
        earnMoreSpecific.avgJobRoleSalary ?? AI_EARN_MORE_MIN_SALARY
      );
      return;
    }

    setShowNotEnoughProfileError(!hasEnoughProfileData);

    const query = result.queries[0]?.query;
    if (!query) {
      onSuggestionError();
      return;
    }

    setIsAnimating(true);
    onChangeKeywords(query);
    onSubmit(
      {
        [SEARCH_STATE_PARAMS.keywords]: query,
        [AI_FABI_TRACKING_TOKEN_PARAM]: result.requestTrackingToken,
      },
      !!initPromptOnLoad
    );
  };

  const onCancelAnimation = (): void => {
    setIsAnimating(false);
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(0, 0);
  };

  return (
    <S.Container>
      {/* @ts-expect-error FIXME: autocomplete never type issues */}
      <S.Input
        variant="inputBar"
        multiline
        textFieldVariant="plain"
        size="large"
        id="query-input"
        data-testid="cs-search-bar-textarea"
        aria-autocomplete="none"
        label={formatMessage({
          id: 'JOBS_SEARCH_INPUT_TITLE_A11Y',
        })}
        menuLabel={formatMessage({
          id: 'JOBS_SEARCH_SWIMPS_TITLE_A11Y',
        })}
        value={truncatedQuery}
        // After fixing the main ts-expect error, the maxLength one will appear
        // TODO: @ts-expect-error maxLength is not recognized
        maxLength={QUERY_MAX_LENGTH}
        ref={inputRef}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        onSelect={handleOnSelect}
        suggestions={value.length === 0 ? suggestions : undefined}
        itemRenderer={suggestionsRenderer}
        isItemDisabled={isItemDisabled}
        clearButtonProps={{
          onMouseDown: (e) => {
            // prevent input focus -> unfocus -> focus
            e.preventDefault();
          },
        }}
      />
      <QueryInputAnimation
        value={value}
        isAnimating={isAnimating}
        onCompleted={onCancelAnimation}
      />
    </S.Container>
  );
};
